import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Search, Filter, Download, Upload, Star, Eye, Edit, Trash2, Copy, 
  Play, Pause, Settings, Users, Clock, Tag, TrendingUp, BarChart3, 
  Grid, List, ChevronDown, ChevronRight, CheckCircle, AlertCircle,
  FileText, GitBranch, Layers, Zap, Target, Activity, RefreshCw
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const WorkflowTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [activeTab, setActiveTab] = useState('templates'); // 'templates', 'testing', 'sharing'
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterComplexity, setFilterComplexity] = useState('all');
  const [sortBy, setSortBy] = useState('downloads'); // 'downloads', 'rating', 'name', 'created'
  const [testResult, setTestResult] = useState(null);
  const [isTesting, setIsTesting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [templatesResponse, categoriesResponse] = await Promise.all([
        MockDataService.getWorkflowTemplates().catch(err => {
          console.error('Error loading templates:', err);
          return { success: false, message: 'Failed to load templates', data: [] };
        }),
        MockDataService.getTemplateCategories().catch(err => {
          console.error('Error loading categories:', err);
          return { success: false, message: 'Failed to load categories', data: [] };
        })
      ]);

      // Ensure templates is always an array
      if (templatesResponse.success) {
        setTemplates(Array.isArray(templatesResponse.data) ? templatesResponse.data : []);
      } else {
        setTemplates([]);
        if (templatesResponse.message) {
          setError(templatesResponse.message);
        }
      }
      
      // Ensure categories is always an array
      if (categoriesResponse.success) {
        setCategories(Array.isArray(categoriesResponse.data) ? categoriesResponse.data : []);
      } else {
        setCategories([]);
      }
    } catch (err) {
      console.error('Error fetching workflow templates data:', err);
      setError('Failed to fetch workflow templates data.');
      // Set empty arrays on error to prevent crashes
      setTemplates([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = useCallback(async (templateData) => {
    try {
      const response = await MockDataService.createWorkflowTemplate(templateData);
      if (response.success) {
        await loadData();
        setShowCreateForm(false);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Failed to create workflow template.');
      console.error('Error creating workflow template:', err);
    }
  }, []);

  const handleUpdateTemplate = useCallback(async (templateId, templateData) => {
    try {
      const response = await MockDataService.updateWorkflowTemplate(templateId, templateData);
      if (response.success) {
        await loadData();
        setSelectedTemplate(null);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Failed to update workflow template.');
      console.error('Error updating workflow template:', err);
    }
  }, []);

  const handleDeleteTemplate = useCallback(async (templateId) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        const response = await MockDataService.deleteWorkflowTemplate(templateId);
        if (response.success) {
          await loadData();
        } else {
          setError(response.message);
        }
      } catch (err) {
        setError('Failed to delete workflow template.');
        console.error('Error deleting workflow template:', err);
      }
    }
  }, []);

  const handleTestTemplate = useCallback(async (templateId) => {
    setIsTesting(true);
    try {
      const response = await MockDataService.testWorkflowTemplate(templateId, {
        ticketCount: 5,
        testScenario: 'normal_flow'
      });
      if (response.success) {
        setTestResult(response.data);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Failed to test workflow template.');
      console.error('Error testing workflow template:', err);
    } finally {
      setIsTesting(false);
    }
  }, []);

  const filteredTemplates = (Array.isArray(templates) ? templates : []).filter(template => {
    if (!template) return false;
    const templateName = (template.name || '').toLowerCase();
    const templateDescription = (template.description || '').toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    const templateTags = Array.isArray(template.tags) ? template.tags : [];
    const matchesSearch = templateName.includes(searchLower) ||
                         templateDescription.includes(searchLower) ||
                         templateTags.some(tag => (tag || '').toLowerCase().includes(searchLower));
    const matchesCategory = filterCategory === 'all' || template.category === filterCategory;
    const matchesComplexity = filterComplexity === 'all' || template.complexity === filterComplexity;
    
    return matchesSearch && matchesCategory && matchesComplexity;
  });

  const sortedTemplates = [...filteredTemplates].sort((a, b) => {
    switch (sortBy) {
      case 'downloads':
        return b.downloads - a.downloads;
      case 'rating':
        return b.rating - a.rating;
      case 'name':
        return a.name.localeCompare(b.name);
      case 'created':
        return new Date(b.createdAt) - new Date(a.createdAt);
      default:
        return 0;
    }
  });

  const getComplexityColor = (complexity) => {
    switch (complexity) {
      case 'simple': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'complex': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getComplexityIcon = (complexity) => {
    switch (complexity) {
      case 'simple': return <CheckCircle size={16} />;
      case 'medium': return <AlertCircle size={16} />;
      case 'complex': return <Zap size={16} />;
      default: return <FileText size={16} />;
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 bg-white rounded-lg shadow-md">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="animate-spin mr-2" size={24} />
          <span>Loading workflow templates...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6 bg-white rounded-lg shadow-md">
        <div className="text-center text-red-600">
          <AlertCircle className="mx-auto mb-2" size={48} />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 bg-white rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <GitBranch className="mr-3" size={28} />
            Workflow Templates
          </h1>
          <p className="text-gray-700 mt-2">
            Browse, customize, and manage pre-built workflow templates for your organization.
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {viewMode === 'grid' ? <List size={16} className="mr-2" /> : <Grid size={16} className="mr-2" />}
            {viewMode === 'grid' ? 'List View' : 'Grid View'}
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
          >
            <Plus size={16} className="mr-2" />
            Create Template
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'templates', name: 'Template Library', icon: GitBranch },
            { id: 'testing', name: 'Template Testing', icon: Play },
            { id: 'sharing', name: 'Template Sharing', icon: Users }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon size={16} className="mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <>
          {/* Search and Filters */}
          <div className="mb-6 space-y-4">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Categories</option>
                {(Array.isArray(categories) ? categories : []).map(category => {
                  if (!category) return null;
                  return (
                    <option key={category.id || Math.random()} value={category.id || category.name}>
                      {category.name || 'Unnamed'} ({category.count || 0})
                    </option>
                  );
                })}
              </select>
              <select
                value={filterComplexity}
                onChange={(e) => setFilterComplexity(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Complexity</option>
                <option value="simple">Simple</option>
                <option value="medium">Medium</option>
                <option value="complex">Complex</option>
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="downloads">Sort by Downloads</option>
                <option value="rating">Sort by Rating</option>
                <option value="name">Sort by Name</option>
                <option value="created">Sort by Created Date</option>
              </select>
            </div>
          </div>

          {/* Templates Grid/List */}
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
            {sortedTemplates && sortedTemplates.length > 0 ? sortedTemplates.map((template) => {
              if (!template) return null;
              return (
              <div
                key={template.id}
                className={`bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow ${
                  viewMode === 'list' ? 'flex items-center p-4' : 'p-6'
                }`}
              >
                {viewMode === 'grid' ? (
                  <>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <GitBranch size={20} className="text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{template.name || 'Unnamed Template'}</h3>
                          <p className="text-sm text-gray-500">{template.category || 'Uncategorized'}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Star size={16} className="text-yellow-400 fill-current" />
                        <span className="text-sm font-medium">{template.rating || 0}</span>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{template.description || 'No description'}</p>
                    
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getComplexityColor(template.complexity || 'simple')}`}>
                          {getComplexityIcon(template.complexity || 'simple')}
                          <span className="ml-1 capitalize">{template.complexity || 'simple'}</span>
                        </span>
                        <span className="text-xs text-gray-500">{template.steps || 0} steps</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        <Clock size={12} className="inline mr-1" />
                        {template.estimatedTime || 'N/A'}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                      <span>{template.downloads || 0} downloads</span>
                      <span>v{template.version || '1.0'}</span>
                    </div>
                    
                    <div className="flex flex-wrap gap-1 mb-4">
                      {(Array.isArray(template.tags) ? template.tags.slice(0, 3) : []).map((tag, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">
                          <Tag size={10} className="mr-1" />
                          {tag || 'Untagged'}
                        </span>
                      ))}
                      {Array.isArray(template.tags) && template.tags.length > 3 && (
                        <span className="text-xs text-gray-500">+{template.tags.length - 3} more</span>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setSelectedTemplate(template)}
                          className="flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
                        >
                          <Eye size={14} className="mr-1" />
                          Preview
                        </button>
                        <button
                          onClick={() => handleTestTemplate(template.id)}
                          disabled={isTesting}
                          className="flex items-center px-3 py-1 text-sm text-green-600 hover:text-green-800 disabled:opacity-50"
                        >
                          <Play size={14} className="mr-1" />
                          Test
                        </button>
                      </div>
                      <div className="flex items-center space-x-1">
                        <button className="p-1 text-gray-400 hover:text-gray-600">
                          <Copy size={14} />
                        </button>
                        <button className="p-1 text-gray-400 hover:text-gray-600">
                          <Download size={14} />
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center space-x-4 flex-1">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <GitBranch size={20} className="text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h3 className="font-semibold text-gray-900">{template.name || 'Unnamed Template'}</h3>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getComplexityColor(template.complexity || 'simple')}`}>
                            {getComplexityIcon(template.complexity || 'simple')}
                            <span className="ml-1 capitalize">{template.complexity || 'simple'}</span>
                          </span>
                          <div className="flex items-center space-x-1">
                            <Star size={14} className="text-yellow-400 fill-current" />
                            <span className="text-sm font-medium">{template.rating || 0}</span>
                          </div>
                        </div>
                        <p className="text-gray-600 text-sm mt-1">{template.description || 'No description'}</p>
                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                          <span>{template.downloads || 0} downloads</span>
                          <span>{template.steps || 0} steps</span>
                          <span>{template.estimatedTime || 'N/A'}</span>
                          <span>v{template.version || '1.0'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setSelectedTemplate(template)}
                        className="flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
                      >
                        <Eye size={14} className="mr-1" />
                        Preview
                      </button>
                      <button
                        onClick={() => handleTestTemplate(template.id)}
                        disabled={isTesting}
                        className="flex items-center px-3 py-1 text-sm text-green-600 hover:text-green-800 disabled:opacity-50"
                      >
                        <Play size={14} className="mr-1" />
                        Test
                      </button>
                      <button className="p-1 text-gray-400 hover:text-gray-600">
                        <Copy size={14} />
                      </button>
                      <button className="p-1 text-gray-400 hover:text-gray-600">
                        <Download size={14} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
            }) : (
              <div className="col-span-full text-center py-12">
                <GitBranch className="mx-auto mb-4 text-gray-400" size={48} />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
                <p className="text-gray-500">Try adjusting your search criteria or create a new template.</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Testing Tab */}
      {activeTab === 'testing' && (
        <div className="space-y-6">
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Template Testing</h3>
            <p className="text-gray-600 mb-4">
              Test workflow templates with sample data to ensure they work correctly before deployment.
            </p>
            
            {testResult && (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Test Results</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{testResult.status}</div>
                    <div className="text-sm text-gray-500">Status</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{testResult.executionTime}ms</div>
                    <div className="text-sm text-gray-500">Execution Time</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{testResult.result.ticketsProcessed}</div>
                    <div className="text-sm text-gray-500">Tickets Processed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{testResult.result.notificationsSent}</div>
                    <div className="text-sm text-gray-500">Notifications Sent</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sharing Tab */}
      {activeTab === 'sharing' && (
        <div className="space-y-6">
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Template Sharing</h3>
            <p className="text-gray-600 mb-4">
              Share your custom workflow templates with your team or the community.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Team Sharing</h4>
                <p className="text-sm text-gray-600 mb-3">Share templates within your organization</p>
                <button className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700">
                  Share with Team
                </button>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Public Sharing</h4>
                <p className="text-sm text-gray-600 mb-3">Publish templates to the community marketplace</p>
                <button className="w-full px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700">
                  Publish Publicly
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowTemplates;
